import type { FastifyReply, FastifyRequest } from 'fastify'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type { SubscriptionPlan } from '@my-roadtrip/types'
import {
  checkoutSessionSchema,
  portalSessionSchema,
  createBillingPlanSchema,
  updateBillingPlanSchema,
  billingMetricsQuerySchema
} from '@my-roadtrip/validators'
import { BillingService } from './billing.service.js'
import { buildDefaultPlans } from './billing.constants.js'
import { ApiResponse } from '../../utils/response'

export class BillingController {
  constructor(private readonly service: BillingService) {}

  async listPlans(request: FastifyRequest<{ Querystring: { includeInactive?: string } }>, reply: FastifyReply) {
    try {
      const includeInactive = request.query.includeInactive === 'true'
      const plans = await this.service.listPlans(includeInactive)
      return reply.send({ plans })
    } catch (error) {
      request.log.error(error, '[Billing] Failed to list plans')
      return ApiResponse.internalError(reply)
    }
  }

  async getSubscription(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.uid) {
      return ApiResponse.unauthorized(reply)
    }

    try {
      const summary = await this.service.getSubscriptionSummary(request.user.uid)
      return reply.send({ summary })
    } catch (error) {
      request.log.error(error, '[Billing] Failed to get subscription summary')
      return ApiResponse.internalError(reply)
    }
  }

  async createCheckoutSession(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.uid) {
      return ApiResponse.unauthorized(reply)
    }

    try {
      const payload = checkoutSessionSchema.parse(request.body)
      const result = await this.service.createCheckoutSession(
        request.user.uid,
        request.user.email || null,
        payload.planId,
        {
          successUrl: payload.successUrl,
          cancelUrl: payload.cancelUrl
        }
      )
      return reply.send(result)
    } catch (error) {
      request.log.error(error, '[Billing] Checkout session failed')
      return ApiResponse.badRequest(reply, 'Checkout session failed')
    }
  }

  async createBillingPortalSession(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.uid) {
      return ApiResponse.unauthorized(reply)
    }

    try {
      const payload = request.body ? portalSessionSchema.parse(request.body) : {}
      const result = await this.service.createBillingPortalSession(
        request.user.uid,
        payload.returnUrl
      )
      return reply.send(result)
    } catch (error) {
      request.log.error(error, '[Billing] Billing portal session failed')
      return ApiResponse.badRequest(reply, 'Billing portal session failed')
    }
  }

  async getQuotaUsage(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.uid) {
      return ApiResponse.unauthorized(reply)
    }

    try {
      const result = await this.service.getSubscriptionSummary(request.user.uid)
      
      // If no result, return default free plan
      if (!result) {
        const defaultPlan = await this.service.getPlan('plan_free')
        return reply.send({ 
          quota: null, 
          plan: defaultPlan || { 
            id: 'plan_free',
            name: 'Gratuit',
            tier: 'free' as const,
            limits: {
              plansPerMonth: 3,
              stopsPerPlan: 10,
              aiCallsPerMonth: 10,
              collaborators: 1,
              storageGB: 1
            }
          }
        })
      }
      
      return reply.send({ quota: result.quota, plan: result.plan })
    } catch (error) {
      request.log.error(error, '[Billing] Failed to fetch quota usage')
      return ApiResponse.internalError(reply)
    }
  }

  async handleWebhook(request: FastifyRequest, reply: FastifyReply) {
    try {
      const signature = request.headers['stripe-signature']
      if (!signature || typeof signature !== 'string') {
        return ApiResponse.badRequest(reply, 'Missing webhook signature')
      }

      const rawBody = getRawBody(request)
      await this.service.handleStripeWebhook(signature, rawBody)
      return reply.send({ received: true })
    } catch (error) {
      request.log.error(error, '[Billing] Stripe webhook error')
      return ApiResponse.badRequest(reply, 'Webhook processing failed')
    }
  }

  async adminListPlans(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.admin) {
      return ApiResponse.forbidden(reply)
    }

    try {
      const plans = await this.service.listPlans(true)
      return reply.send({ plans })
    } catch (error) {
      request.log.error(error, '[Billing] Admin list plans failed')
      return ApiResponse.internalError(reply)
    }
  }

  async adminCreatePlan(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.admin) {
      return ApiResponse.forbidden(reply)
    }

    try {
      const payload = createBillingPlanSchema.parse(request.body)
      const defaults = buildDefaultPlans().find(plan => plan.id === payload.id)
      const displayOrder =
        typeof payload.displayOrder === 'number'
          ? payload.displayOrder
          : defaults?.displayOrder ?? 0
      const plan: SubscriptionPlan = {
        ...(defaults ?? payload),
        ...payload,
        displayOrder,
        createdAt: nowISOString(payload.createdAt),
        updatedAt: nowISOString(payload.updatedAt)
      }
      const savedPlan = await this.service.upsertPlan(plan, { syncStripe: true })
      return reply.send({ plan: savedPlan })
    } catch (error) {
      request.log.error(error, '[Billing] Admin create plan failed')
      return ApiResponse.badRequest(reply, 'Failed to create plan')
    }
  }

  async updatePlan(request: FastifyRequest<{ Params: { id: string } }>, reply: FastifyReply) {
    if (!request.user?.admin) {
      return ApiResponse.forbidden(reply)
    }

    try {
      const planId = request.params.id
      const payload = updateBillingPlanSchema.parse(request.body)
      const existing = await this.service.getPlan(planId)
      const plan = {
        ...existing,
        ...payload,
        id: planId,
        displayOrder:
          typeof payload.displayOrder === 'number'
            ? payload.displayOrder
            : existing?.displayOrder ?? 0,
        updatedAt: nowISOString(payload.updatedAt)
      }
      const savedPlan = await this.service.upsertPlan(plan, { syncStripe: true })
      return reply.send({ plan: savedPlan })
    } catch (error) {
      request.log.error(error, '[Billing] Admin update plan failed')
      return ApiResponse.badRequest(reply, 'Failed to update plan')
    }
  }

  async deletePlan(request: FastifyRequest<{ Params: { id: string } }>, reply: FastifyReply) {
    if (!request.user?.admin) {
      return ApiResponse.forbidden(reply)
    }

    try {
      const planId = request.params.id
      await this.service.deletePlan(planId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, '[Billing] Admin delete plan failed')
      return ApiResponse.internalError(reply)
    }
  }

  async adminMetrics(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.admin) {
      return ApiResponse.forbidden(reply)
    }

    try {
      const query = billingMetricsQuerySchema.parse(request.query ?? {})
      const metrics = await this.service.getBillingMetrics()
      return reply.send({ metrics, period: query })
    } catch (error) {
      request.log.error(error, '[Billing] Admin metrics failed')
      return ApiResponse.internalError(reply)
    }
  }

  async generateReferralCode(request: FastifyRequest, reply: FastifyReply) {
    if (!request.user?.uid) {
      return ApiResponse.unauthorized(reply)
    }

    try {
      const referral = await this.service.generateReferralCode(request.user.uid)
      return reply.send({ referral })
    } catch (error) {
      request.log.error(error, '[Billing] Referral generation failed')
      return ApiResponse.internalError(reply)
    }
  }

  async referralLeaderboard(request: FastifyRequest<{ Querystring: { limit?: string } }>, reply: FastifyReply) {
    try {
      const parsedLimit = Number.parseInt(request.query.limit || '10', 10)
      const limit = !Number.isNaN(parsedLimit) && parsedLimit > 0 && parsedLimit <= 100 ? parsedLimit : 10
      const leaderboard = await this.service.getReferralLeaderboard(limit)
      return reply.send({ leaderboard })
    } catch (error) {
      request.log.error(error, '[Billing] Referral leaderboard failed')
      return ApiResponse.internalError(reply)
    }
  }
}

function nowISOString(existing?: string): string {
  if (existing && isString(existing)&& existing.length > 0) {
    return existing
  }
  return new Date().toISOString()
}

function getRawBody(request: FastifyRequest): Buffer {
  const candidate = (request as FastifyRequest & { rawBody?: string | Buffer }).rawBody
  if (isString(candidate)) {
    return Buffer.from(candidate)
  }
  if (candidate instanceof Buffer) {
    return candidate
  }
  if (request.body) {
    return Buffer.from(JSON.stringify(request.body))
  }
  return Buffer.from('')
}
